import React, { createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

const ModalContext = createContext();

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

export const ModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [item, setItem] = useState(null);
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const [currentQuantity, setCurrentQuantity] = useState(1);
  const [existingQuantity, setExistingQuantity] = useState(0);
  // The cart row being re-customised, so the footer can replace it in place
  // rather than add a second one (#583).
  const [existingCartItemId, setExistingCartItemId] = useState(null);

  // Open modal with item data
  const openModal = useCallback((itemData, existingCartItem = null) => {
    setItem(itemData);

    if (existingCartItem) {
      setSelectedModifiers(existingCartItem.modifiers || {});
      setCurrentQuantity(existingCartItem.quantity || 1);
      setExistingQuantity(existingCartItem.quantity || 0);
      setExistingCartItemId(existingCartItem.cartItemId || null);
    } else {
      setSelectedModifiers({});
      setCurrentQuantity(1);
      setExistingQuantity(0);
      setExistingCartItemId(null);
    }

    setIsOpen(true);
  }, []);

  // Close modal and reset state
  const closeModal = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setItem(null);
      setSelectedModifiers({});
      setCurrentQuantity(1);
      setExistingQuantity(0);
      setExistingCartItemId(null);
    }, 300); // Wait for close animation
  }, []);

  // Handle modifier selection
  const handleModifierChange = useCallback((groupId, selection) => {
    setSelectedModifiers((prev) => ({
      ...prev,
      [groupId]: selection,
    }));
  }, []);

  // Handle quantity changes
  const incrementQuantity = useCallback(() => {
    setCurrentQuantity((prev) => prev + 1);
  }, []);

  const decrementQuantity = useCallback(() => {
    setCurrentQuantity((prev) => Math.max(1, prev - 1));
  }, []);

  const setQuantity = useCallback((quantity) => {
    setCurrentQuantity(Math.max(1, quantity));
  }, []);

  // Calculate derived values
  const calculateTotalPrice = useCallback(() => {
    if (!item) return 0;

    let total = item.price || 0;

    Object.entries(selectedModifiers).forEach(([groupId, selection]) => {
      const group = item.modifierGroups?.find((g) => g.id === groupId);
      if (group) {
        const selections = Array.isArray(selection) ? selection : [selection];
        selections.forEach((modId) => {
          const modifier = group.modifiers.find((m) => m.id === modId);
          if (modifier?.price) {
            total += modifier.price;
          }
        });
      }
    });

    return total * currentQuantity;
  }, [item, selectedModifiers, currentQuantity]);

  const isValidSelection = useCallback(() => {
    if (!item?.modifierGroups) return true;

    return item.modifierGroups
      .filter((g) => g.required)
      .every((group) => {
        const selection = selectedModifiers[group.id];
        const count = !selection
          ? 0
          : Array.isArray(selection)
            ? selection.length
            : 1;
        return count >= (group.minSelections || 1);
      });
  }, [item, selectedModifiers]);

  const value = {
    // State
    isOpen,
    item,
    selectedModifiers,
    currentQuantity,
    existingQuantity,
    existingCartItemId,

    // Actions
    openModal,
    closeModal,
    handleModifierChange,
    incrementQuantity,
    decrementQuantity,
    setQuantity,

    // Computed values
    calculateTotalPrice,
    isValidSelection,
    existingInCart: existingQuantity > 0,
  };

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
};

ModalProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ModalContext;
